import React, { useContext } from 'react'
import {
    Divider,
    useMediaQuery,
    InputAdornment,
    IconButton,
    TextField,
    MenuItem,
    Select,
    Grid,
    Checkbox,
} from '@material-ui/core'
import FlexView from 'react-flexview/lib'
import {
    FirebaseService,
    FirebaseConfigKey,
} from '../../services/FirebaseService'
import EventIcon from '@material-ui/icons/Event'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { ClaimContextStore, ACTIONS } from '../../context/ClaimContext'
import {
    EventEmitterService,
    EventKey,
} from '../../services/EventEmitterService'
import { ClaimType } from '../../models/claim/ClaimType'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet'
import './ClaimEnter.scss'
import HelpIcon from '@material-ui/icons/Help'
import { ClaimReason } from '../../models/claim/ClaimReason'
import { BackButton } from '../Core/Buttons/BackButton'
import { ContinueButton } from '../Core/Buttons/ContinueButton'
import { Action } from '../../models/Action'
import dayjs from 'dayjs'
import { ClaimEquipmentView } from '../Core/Hull/ClaimEquipmentView'
import { ClaimDroneView } from '../Core/Hull/ClaimDroneView'

const getDefaultState = (drones, equipment) => {
    let obj = {}

    drones.map((item) => {
        obj['drone_' + item.id] = item.damage_description
    })

    equipment.map((item) => {
        obj['equip_' + item.id] = item.damage_description
    })

    return obj
}

export const ClaimHullInfo = () => {
    const claimContext = useContext(ClaimContextStore)

    const isMobileByWidth = !useMediaQuery((theme) =>
        theme.breakpoints.up('md')
    )
    const { register, getValues, trigger, control, errors, watch } = useForm({
        defaultValues: getDefaultState(
            claimContext.claim_hull_info.drones,
            claimContext.claim_hull_info.equipment
        ),
        mode: 'onChange',
    })
    const [selectedDrones, setSelectedDrones] = React.useState(
        claimContext.claim_hull_info.drones
    )
    const [selectedEquipment, setSelectedEquipment] = React.useState(
        claimContext.claim_hull_info.equipment
    )
    const [continueError, setContinueError] = React.useState('')

    const onContinue = async () => {
        let valid = await trigger()

        if (!valid) return

        if (!validateSelectedHull()) {
            setContinueError('Must select at least one equipment or drone')
            return
        }

        claimContext.dispatch(
            new Action(ACTIONS.SET_HULL_INFO, {
                drones: populateDamageDescriptionFromForm(
                    selectedDrones,
                    'drone'
                ),
                equipment: populateDamageDescriptionFromForm(
                    selectedEquipment,
                    'equip'
                ),
            })
        )

        EventEmitterService.dispatch(EventKey.CLAIM_HULL_INFO_FINISHED)
    }

    const populateDamageDescriptionFromForm = (lst, prefix) => {
        return lst.map((item) =>
            Object.assign({}, item, {
                damage_description: getValues()[`${prefix}_${item.id}`],
            })
        )
    }

    const validateSelectedHull = () => {
        if (
            claimContext.claim_incident_info.claim_type == ClaimType.HULL ||
            claimContext.claim_incident_info.claim_type ==
                ClaimType.HULL_AND_LIABILITY
        ) {
            return selectedDrones.length > 0 || selectedEquipment.length > 0
        }
        return true
    }

    const dronesFilter = (drone) => {
        if (claimContext.selected_policy.physicalDamageExtensions.length > 0) {
            for (
                var i = 0;
                i <
                claimContext.selected_policy.physicalDamageExtensions.length;
                i++
            ) {
                let extension =
                    claimContext.selected_policy.physicalDamageExtensions[i]
                if (
                    extension.insuredDroneList.find((eItem) => {
                        return eItem.id == drone.id
                    })
                ) {
                    return dayjs(extension.validFromUtc).isBefore(
                        claimContext.date_happen
                    )
                }
            }
            return true
        } else return true
    }

    const equipmentFilter = (equip) => {
        if (claimContext.selected_policy.physicalDamageExtensions.length > 0) {
            for (
                var i = 0;
                i <
                claimContext.selected_policy.physicalDamageExtensions.length;
                i++
            ) {
                let extension =
                    claimContext.selected_policy.physicalDamageExtensions[i]
                if (
                    extension.insuredEquipmentList.find((eItem) => {
                        return eItem.id == equip.id
                    })
                ) {
                    return dayjs(extension.validFromUtc).isBefore(
                        claimContext.date_happen
                    )
                }
            }
            return true
        } else return true
    }

    const selectDrone = (droneId) => {
        let index = findIndex(selectedDrones, droneId)
        if (index >= 0)
            setSelectedDrones([
                ...selectedDrones.slice(0, index),
                ...selectedDrones.slice(index + 1),
            ])
        else
            setSelectedDrones([
                ...selectedDrones,
                { id: droneId, damage_description: '' },
            ])
    }

    const findIndex = (lst: [], id) => {
        return lst.findIndex((i) => i.id == id)
    }

    const inList = (lst: [], id) => {
        return findIndex(lst, id) >= 0
    }

    const selectEquipment = (equipmentId) => {
        let index = findIndex(selectedEquipment, equipmentId)
        if (index >= 0)
            setSelectedEquipment([
                ...selectedEquipment.slice(0, index),
                ...selectedEquipment.slice(index + 1),
            ])
        else
            setSelectedEquipment([
                ...selectedEquipment,
                { id: equipmentId, damage_description: '' },
            ])
    }

    React.useEffect(() => {
        if (
            (selectedDrones.length > 0 || selectedEquipment.length > 0) &&
            continueError == 'Must select at least one equipment or drone'
        )
            setContinueError('')
    }, [selectedDrones, selectedEquipment])

    React.useEffect(() => {
        if (
            claimContext.selected_policy.insuredDrones.filter(dronesFilter)
                .length == 0 &&
            claimContext.selected_policy.insuredEquipment.filter(
                equipmentFilter
            ) == 0
        )
            setContinueError('This policy has no hull')
    }, [claimContext.selected_policy])

    return (
        <>
            <br />
            <div className="text-title blue bold">Equipment Info</div>
            {!isMobileByWidth && <Divider />}
            <div className="text-body gray margin-top-6">
                Select the item(s) you wish to make a claim for
            </div>
            <br />
            {claimContext.selected_policy.insuredDrones
                .filter(dronesFilter)
                .map((drone, index) => (
                    <FlexView
                        key={index}
                        className="margin-top-basic full-width"
                    >
                        <Checkbox
                            color={'primary'}
                            checked={inList(selectedDrones, drone.id)}
                            onClick={() => selectDrone(drone.id)}
                        />
                        <ClaimDroneView
                            drone={drone}
                            droneDeductible={drone.deductible}
                        />
                        {inList(selectedDrones, drone.id) && (
                            <TextField
                                className="margin-left-basic dmg-desc-txt"
                                error={!!errors[`drone_${drone.id}`]}
                                helperText={
                                    !!errors[`drone_${drone.id}`] &&
                                    errors[`drone_${drone.id}`].type
                                }
                                variant="outlined"
                                label="Damage Description"
                                placeholder="Describe the damage"
                                name={`drone_${drone.id}`}
                                inputRef={register({ required: true })}
                            />
                        )}
                    </FlexView>
                ))}
            {claimContext.selected_policy.insuredEquipment
                .filter(equipmentFilter)
                .map((equip, index) => (
                    <FlexView
                        key={index}
                        className="margin-top-basic full-width"
                    >
                        <Checkbox
                            color={'primary'}
                            checked={inList(selectedEquipment, equip.id)}
                            onClick={() => selectEquipment(equip.id)}
                        />
                        <ClaimEquipmentView
                            equipment={equip}
                            equipmentDeductible={equip.deductible}
                        />
                        {inList(selectedEquipment, equip.id) && (
                            <TextField
                                fullWidth
                                className="margin-left-basic dmg-desc-txt"
                                error={!!errors[`equip_${equip.id}`]}
                                helperText={
                                    !!errors[`equip_${equip.id}`] &&
                                    errors[`equip_${equip.id}`].type
                                }
                                variant="outlined"
                                label="Damage Description"
                                placeholder="Describe the damage"
                                name={`equip_${equip.id}`}
                                inputRef={register({ required: true })}
                            />
                        )}
                    </FlexView>
                ))}
            <br />
            <div className="text-body red margin-top-6">{continueError}</div>
            <FlexView className="margin-top-basic">
                <BackButton />
                <ContinueButton onClick={onContinue} />
            </FlexView>
        </>
    )
}
